var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// src/hooks/useAccessToken.ts
import useSWR from 'swr';
import { useAuth0 } from '@auth0/auth0-react';
// Function to fetch the access token
const fetchAccessToken = (getAccessTokenSilently) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const token = yield getAccessTokenSilently();
        return token;
    }
    catch (error) {
        console.error('Error fetching access token', error);
        throw error;
    }
});
// Custom hook to use the access token
export default function useAccessToken() {
    const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
    const localStorageKey = 'auth-access-token';
    const cacheTimestampKey = 'auth-access-token-timestamp';
    const fetchWithLocalStorage = () => __awaiter(this, void 0, void 0, function* () {
        const cachedToken = localStorage.getItem(localStorageKey);
        const cacheTimestamp = localStorage.getItem(cacheTimestampKey);
        if (cachedToken &&
            cacheTimestamp &&
            Date.now() - parseInt(cacheTimestamp, 10) < 10 * 1000 // 10 seconds
        ) {
            return cachedToken;
        }
        const token = yield fetchAccessToken(getAccessTokenSilently);
        // Store the new token in localStorage
        localStorage.setItem(localStorageKey, token);
        localStorage.setItem(cacheTimestampKey, Date.now().toString());
        return token;
    });
    // Use SWR to cache and manage the access token
    const { data: accessToken, isLoading: tokenLoading, error, isValidating, mutate } = useSWR(isAuthenticated ? 'accessToken' : null, fetchWithLocalStorage, {
        revalidateOnFocus: false, // Disable revalidation on window focus
        shouldRetryOnError: true, // Retry fetching on error
        dedupingInterval: 10 * 1000, // Cache the token for 10 seconds
        errorRetryCount: 3, // Retry fetching up to 3 times
    });
    // Manual refresh function
    const refreshToken = () => __awaiter(this, void 0, void 0, function* () {
        try {
            const token = yield fetchAccessToken(getAccessTokenSilently);
            localStorage.setItem(localStorageKey, token);
            localStorage.setItem(cacheTimestampKey, Date.now().toString());
            mutate(token, false); // Update SWR cache without refetching
        }
        catch (error) {
            console.error('Error refreshing access token', error);
        }
    });
    return {
        accessToken,
        isLoading: isLoading || tokenLoading,
        isError: !!error,
        isValidating,
        refreshToken, // Function to manually refresh the token
    };
}
